import React from 'react'
import Box from '@mui/material/Box'
import CustomChip from 'src/@core/components/mui/chip'

export type RestaurantStatusSource = {
  isActive?: boolean | number | null
  isTest?: boolean | number | null
  isPermanentlyClosed?: boolean | number | null
} | null

function isTruthy(v: unknown): boolean {
  return Boolean(v)
}

/**
 * Dot + status chip aligned with restaurant detail header (`layout.tsx` getStatusDisplay).
 * Used in BHYBE breadcrumbs after the venue name.
 */
export const RestaurantBreadcrumbStatus: React.FC<{ restaurant: RestaurantStatusSource }> = ({ restaurant }) => {
  if (!restaurant) {
    return null
  }

  const permanentlyClosed = isTruthy(restaurant.isPermanentlyClosed)
  const active = isTruthy(restaurant.isActive)
  const test = isTruthy(restaurant.isTest)

  const dotAndChip = (dotBg: string, label: string, color: 'error' | 'warning' | 'success') => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        component='span'
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: dotBg
        }}
      />
      <CustomChip rounded size='small' skin='light' label={label} color={color} sx={{ marginLeft: '0 !important' }} />
    </Box>
  )

  if (permanentlyClosed) {
    return dotAndChip('error.main', 'Permanently Closed', 'error')
  }

  if (!active) {
    return dotAndChip('error.main', 'Inactive', 'error')
  }

  if (active && test) {
    return dotAndChip('warning.main', 'Test Mode', 'warning')
  }

  if (active && !test) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component='span'
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: 'success.main',
            '@keyframes bhyveLivePulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
              '70%': { boxShadow: '0 0 0 8px rgba(76, 175, 80, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' }
            },
            animation: 'bhyveLivePulse 1.5s infinite'
          }}
        />
        <CustomChip
          rounded
          size='small'
          skin='light'
          label='Live'
          color='success'
          sx={{ marginLeft: '0 !important' }}
        />
      </Box>
    )
  }

  return dotAndChip('error.main', 'Inactive', 'error')
}
